import React, { useContext, useState } from 'react';

import {
  AS_ADDITION,
  AS_INSTRUCTION,
  CONFIDENCE,
  GRANDPARENT_NAMES,
  IS_NESTED_MODIFIER,
  MAPPING_STATUS_COLUMN,
  MODIFIER_GROUP,
  NAME_OBJECT_KEY,
  PARENT_NAMES,
  PRICE,
  REPLACES_MAIN,
  UUID_EXTERNAL_ID,
  UUID_NAME,
  EXTERNAL_ID_MAPPING,
  IGNORE,
} from 'constants/mapping';
import { CAMEL_TYPES } from 'constants/menuManagement';

import { findMappingStatus, formatConfidence } from 'helpers/mapping';

import { MenuMappingContext } from 'components/MenuManagement/MenuEdit/MenuMapping/MenuMapping';
import MappingActionButtons from 'components/Mapping/Tabs/MappingActionButtons';
import PanelContent from 'components/Mapping/TableView/PanelContent';

export default function ModifierTable() {
  const [filteredName, setFilteredName] = useState('');
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  // categories correspond to mod groups for modifiers
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredParents, setFilteredParents] = useState([]);
  const [sortFilters, setSortFilters] = useState({ order_by: '', sort_order: '' });

  const handleSort = orderBy => {
    if (orderBy === sortFilters.order_by) {
      setSortFilters({
        order_by: orderBy,
        sort_order: sortFilters.sort_order === 'asc' ? 'desc' : 'asc',
      });
    } else {
      setSortFilters({ order_by: orderBy, sort_order: 'asc' });
    }
  };

  const columns = [
    {
      key: 'status',
      title: 'Status',
    },
    {
      key: 'cn_name',
      title: 'CN Name',
      labelKey: 'uuidName',
      sorter: handleSort,
      filters: sortFilters,
    },
    {
      key: 'price',
      title: 'Price',
      labelKey: 'price',
      sorter: handleSort,
      filters: sortFilters,
    },
    {
      key: 'modifier_group',
      labelKey: 'parentNames',
      title: 'CN mod group',
      sorter: handleSort,
      filters: sortFilters,
    },
    {
      key: 'cn_parent',
      labelKey: 'grandparentNames',
      title: 'CN parent',
      sorter: handleSort,
      filters: sortFilters,
    },
    {
      key: 'pos_item',
      title: 'POS item detail',
    },
    {
      key: 'match',
      title: 'Match',
      labelKey: 'confidence',
      sorter: handleSort,
      filters: sortFilters,
    },
    {
      key: 'flag',
      title: 'Flag',
    },
    {
      key: 'edit',
      title: 'Edit',
    },
  ];

  function sortByNumberOfItems(modifierList) {
    let modList = modifierList;
    if (sortFilters.sort_order === 'asc') {
      modList = modList.sort((a, b) => {
        if (a[sortFilters.order_by].length < b[sortFilters.order_by].length) return -1;
        if (a[sortFilters.order_by].length > b[sortFilters.order_by].length) return 1;
        return 0;
      });
    } else {
      modList = modList.sort((a, b) => {
        if (a[sortFilters.order_by].length > b[sortFilters.order_by].length) return -1;
        if (a[sortFilters.order_by].length < b[sortFilters.order_by].length) return 1;
        return 0;
      });
    }
    return modList;
  }

  function sortByString(modifierList) {
    let modList = modifierList;
    if (sortFilters.sort_order === 'asc') {
      modList = modList.sort((a, b) => {
        if (a[sortFilters.order_by] < b[sortFilters.order_by]) return -1;
        if (a[sortFilters.order_by] > b[sortFilters.order_by]) return 1;
        return 0;
      });
    } else {
      modList = modList.sort((a, b) => {
        if (a[sortFilters.order_by] > b[sortFilters.order_by]) return -1;
        if (a[sortFilters.order_by] < b[sortFilters.order_by]) return 1;
        return 0;
      });
    }
    return modList;
  }

  const { appMenu, posMenu, formatPrice, renderMappingActionButtons } =
    useContext(MenuMappingContext);

  const formatData = (() => {
    if (!appMenu[CAMEL_TYPES.modifiers]) return [];
    let modifierList = Object.values(appMenu[CAMEL_TYPES.modifiers]);

    if (filteredName) {
      modifierList = modifierList.filter(item =>
        item[UUID_NAME].toLowerCase().includes(filteredName.toLowerCase()),
      );
    }

    if (sortFilters.order_by) {
      if (sortFilters.order_by === 'grandparentNames') {
        modifierList = sortByNumberOfItems(modifierList);
      } else if (sortFilters.order_by !== 'grandparentNames') {
        modifierList = sortByString(modifierList);
      }
    }

    if (filteredStatuses.length) {
      modifierList = modifierList.filter(item =>
        filteredStatuses.includes(
          findMappingStatus({
            item,
            posMenu,
            itemType: CAMEL_TYPES.modifiers,
          }),
        ),
      );
    }

    if (filteredCategories.length) {
      modifierList = modifierList.filter(item =>
        filteredCategories.includes(item[PARENT_NAMES][0]),
      );
    }

    if (filteredParents.length) {
      modifierList = modifierList.filter(item =>
        item[GRANDPARENT_NAMES].some(parent => filteredParents.includes(parent)),
      );
    }

    return modifierList.map(item => {
      if (!item) return {};
      /* setAppToPosMain is a flag that the user can set to map a main item in the
POS to a modifier in the apps
this should also be automatically set if there is already a main set on the item */
      return {
        key: item.uuidUuid,
        [NAME_OBJECT_KEY]: item.uuidName,
        [MODIFIER_GROUP]: item[PARENT_NAMES],
        [GRANDPARENT_NAMES]: item[GRANDPARENT_NAMES],
        [PRICE]: formatPrice(item[PRICE]),
        [IS_NESTED_MODIFIER]: item[IS_NESTED_MODIFIER],
        [UUID_EXTERNAL_ID]: item[UUID_EXTERNAL_ID],
        [EXTERNAL_ID_MAPPING]: item[EXTERNAL_ID_MAPPING],
        [MAPPING_STATUS_COLUMN]: findMappingStatus({
          item,
          posMenu,
          itemType: CAMEL_TYPES.modifiers,
        }),
        [AS_ADDITION]: item[AS_ADDITION],
        [AS_INSTRUCTION]: item[AS_INSTRUCTION],
        [IGNORE]: item[IGNORE],
        [REPLACES_MAIN]: item[REPLACES_MAIN],
        [CONFIDENCE]: formatConfidence(item),
      };
    });
  })();

  return (
    <>
      <PanelContent
        filteredName={filteredName}
        setFilteredName={setFilteredName}
        setFilteredStatuses={setFilteredStatuses}
        setFilteredCategories={setFilteredCategories}
        setFilteredParents={setFilteredParents}
        columns={columns}
        dataSource={formatData}
        isModifierSelected
      />
      <MappingActionButtons>{renderMappingActionButtons}</MappingActionButtons>
    </>
  );
}

ModifierTable.propTypes = {};
